/* eslint-disable no-unused-vars */
/* global _TL_ */
import Backbone from 'backbone';
import _ from 'underscore';
import { ThemeProvider } from 'react-jss';
import React from 'react';
import ReactDOM from 'react-dom';
import config from '@bingads-webui-component-legacy/config/index';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { AdEditor } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { getVerifiedTrackingSettings } from '@bingads-webui-campaign-react/xandr-display-ads-campaign-shared-components';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { newVideoFromGridObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { isXandrServing } from '@bingads-webui-campaign-react/constants';

export default class View extends Backbone.View {
  initialize({
    scope,
    editMode = false,
    model = {},
    showInline = false,
    instrumentation,
    perfMarker,
    timeZone,
    permissions,
    logActivity = _.noop,
    activityLogger,
    campaignId,
    adGroupId,
    serverContext,
    preferenceService,
    biddingStrategyType = '', // bidding scheme from the page when users select the exact campaign or adgroup
    triggerCancel,
    triggerClose,
    handleSave,
    isMultiAdFormatAd,
    loadClipchampPlayer,
  }) {
    this.scope = scope;
    this.editMode = editMode;
    this.showInline = showInline;
    this.perfMarker = perfMarker;
    this.instrumentation = instrumentation;
    this.model = model;
    this.apiErrors = [];
    this.campaignId = campaignId || this.scope().campaignId;
    this.adGroupId = adGroupId || this.scope().adGroupId;
    this.activityLogger = activityLogger;
    this.resetFields = false;
    this.permissions = permissions;
    this.serverErrors = null;
    this.serverContext = serverContext;
    this.allowedVideoTypes = ['VideoMedia16X9'];
    this.preferenceService = preferenceService;
    this.savedAdVideo = (model && model.Videos && this.getVideoAdModel(model.Videos)) || null;
    this.verifiedTrackingSetting = this.editMode ? getVerifiedTrackingSettings(model) : {};
    this.triggerCancel = triggerCancel;
    this.triggerClose = triggerClose;
    this.handleSave = handleSave;
    this.scenario = 'Edit-VideoAds';
    this.biddingScheme = this.getBiddingScheme({ model, editMode, directBiddingStrategyType: biddingStrategyType });
    this.campaignSettings = this.getCampaignSettings({ model, editMode });
    this.campaignSubType = this.getCampaignSubType({ model, editMode });
    this.isXandrServingCampaign = isXandrServing(
      this.campaignSubType,
      this.campaignSettings);
    this.savedAdModel = {
      isValid: true,
      isDirty: false,
      data: [{
        isDirty: false,
        isValid: true,
        data: {
          FinalUrls: this.editMode ? model && model.FinalUrls : [],
          TrackingUrlTemplate: this.editMode ? model && model.TrackingUrlTemplate : '',
          FinalUrlSuffix: this.editMode ? model && model.FinalUrlSuffix : '',
          UrlCustomParameters: this.editMode ? model && model.UrlCustomParameters : {},
          ImpressionTrackingUrls: this.editMode ? model && model.ImpressionTrackingUrls : [],
          video: this.editMode ? newVideoFromGridObject(this.savedAdVideo) : null,
          ...this.verifiedTrackingSetting,
        },
        uid: Math.random(),
        Id: this.editMode ? model.Id : null,
      }],
      currentPageIndex: 1,
    };

    this.isMultiAdFormatAd = isMultiAdFormatAd;
    this.loadClipchampPlayer = loadClipchampPlayer;

    this.scenario = 'Edit-BrandAwarnessVideoAds'// eslint-disable-line

    this.updatedAdModel = this.savedAdModel;

    this.oDataConfig = {
      odataRootUrl: config.get('CampaignAzureDomain'),
      odataPath: config.get('odata.url') + config.get('odata.path'),
      odataToken: config.get('odata.token'),
      oDataToken: config.get('odata.token'),
      oDataPath: config.get('odata.url') + config.get('odata.path'),
    };

    this.theme = config.get('theme');
    this.aid = _.result(config.get('CurrentAccount'), 'Id');
    this.cid = _.result(config.get('CurrentCustomer'), 'Id');
  }

  getBiddingScheme({ model, editMode, directBiddingStrategyType = '' }) {
    if (editMode) {
      return _.get(model, 'BiddingScheme');
    }

    // ad panel is from ad page with exact campaign or campaign with adgroup
    if (!_.isEmpty(directBiddingStrategyType)) {
      return { Type: directBiddingStrategyType };
    }

    const biddingStrategyType = _.get(this.scope(), 'biddingStrategyType');

    return _.isString(biddingStrategyType) && !_.isEmpty(biddingStrategyType) ? { Type: biddingStrategyType } : {};
  }

  getCampaignSubType({ model, editMode }) {
    if (editMode) {
      return _.get(model, ['Campaign', 'CampaignSubType'], '');
    }

    const campaignSubType = _.get(this.scope(), 'campaignSubType');

    if (_.isEmpty(campaignSubType)) {
      return '';
    }

    return campaignSubType;
  }

  getCampaignSettings({ model, editMode }) {
    if (editMode) {
      return _.get(model, ['Campaign', 'CampaignSettings'], []);
    }

    const campaignSettings = _.get(this.scope(), 'campaignSettings');

    if (_.isEmpty(campaignSettings)) {
      return [];
    }

    return campaignSettings;
  }

  getVideoAdModel(videos) {
    if (!videos) {
      return null;
    }

    const parsedVideos = _.filter(videos, (video) => {
      return this.allowedVideoTypes.includes(video.Asset.SubType);
    });

    return parsedVideos.length > 0 ? parsedVideos[0].Asset : null;
  }

  onAdsValidation(result) {
    this.savedAdModel = { ...result };
    this.render();
  }

  validate() {
    return this.savedAdModel.isValid;
  }

  toObject() {
    const params = { Videos: this.savedAdModel.data[0].data.video, FinalUrls: this.savedAdModel.data[0].data.FinalUrls };

    return params;
  }

  clearApiErrors() {
    this.apiErrors = [];
  }

  clearFields() {
    this.editMode = false;
    this.model = {};
    this.resetFields = !this.resetFields;
    this.savedAdModel = {
      isValid: true,
      isDirty: false,
      data: [{
        isDirty: false,
        isValid: true,
        data: {
          FinalUrls: [],
          video: null,
        },
        Id: null,
      }],
      currentPageIndex: 1,
    };

    this.render();
  }

  onSave(result) {
    this.triggerClose();
    return this.handleSave(result.ads.data[0], result.ads.data[0].Id, 'Responsive');
  }

  onSaveAndCreateAnother(result) {
    ReactDOM.unmountComponentAtNode(this.el);
    this.clearFields();

    this.render();
    return this.handleSave(result.ads.data[0], result.ads.data[0].Id, 'Responsive');

  }

  remove() {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }

  cancel() {
    this.triggerCancel();
    this.triggerClose();
  }

  render() {
    const scenarioName = 'XandrVideoAdsAdEditing';
    const activityName = 'Ad';

    ReactDOM.render(
      // eslint-disable-next-line no-extra-parens
      (
        <ThemeProvider theme={this.theme}>
          <I18nContext.Provider value={this.options.newI18n}>
            <InstrumentationContext.Provider value={this.instrumentation}>
              <ScopeContext.Provider value={{ scope: { values: { ...this.scope(), cid: this.cid, aid: this.aid } } }}>
                <Scenario name={scenarioName}>
                  <Activity name={activityName}>
                    <ServerContext.Provider value={this.serverContext}>
                      <PermissionsContext.Provider value={this.permissions}>
                        <PreferenceContext.Provider value={this.preferenceService}>
                          <AdEditor
                            aid={this.aid}
                            cid={this.cid}
                            campaignId={this.campaignId}
                            adGroupId={this.adGroupId}
                            i18n={this.options.newI18n}
                            perfMarker={this.perfMarker}
                            permissions={this.permissions}
                            onAdsValidation={this.onAdsValidation.bind(this)}
                            oDataConfig={this.oDataConfig}
                            resetFields={this.resetFields}
                            adsModel={this.savedAdModel}
                            serverErrors={this.serverErrors}
                            biddingScheme={this.biddingScheme}
                            isXandrServingCampaign={this.isXandrServingCampaign}
                            onCancel={_.bind(this.cancel, this)}
                            onSave={_.bind(this.onSave, this)}
                            onSaveAndCreateAnother={_.bind(this.onSaveAndCreateAnother, this)}
                            isMultiAdFormatAd={this.isMultiAdFormatAd}
                            loadClipchampPlayer={this.loadClipchampPlayer}
                          />
                        </PreferenceContext.Provider>
                      </PermissionsContext.Provider>
                    </ServerContext.Provider>
                  </Activity>
                </Scenario>
              </ScopeContext.Provider>
            </InstrumentationContext.Provider>
          </I18nContext.Provider>
        </ThemeProvider>
      ),
      this.el,
    );

    return this;

  }
}
